/**
 * Analytics della demo — integrazione Umami.
 *
 * Tutta la telemetria passa da qui: nei componenti non devono comparire
 * chiamate dirette a `window.umami`.
 *
 * Regole applicate:
 *  - lo script viene caricato SOLO se il tracking è abilitato e sia lo script
 *    URL sia il website ID sono valorizzati (vedi `isEnabled`);
 *  - se lo script non si carica (rete assente, ad-blocker, sviluppo locale)
 *    l'applicazione continua a funzionare: ogni chiamata è protetta;
 *  - gli eventi inviati prima che `window.umami` sia pronto vengono messi in
 *    coda e rispediti al caricamento;
 *  - i dati allegati agli eventi sono filtrati da `sanitizeEventData`, che
 *    accetta solo valori categoriali brevi: niente nomi, email, password,
 *    token, telefoni, ragioni sociali, contenuti di preventivi, prezzi o
 *    importi può passare, nemmeno per errore.
 *
 * Configurazione (.env, prefisso Vue CLI):
 *   VUE_APP_UMAMI_ENABLED=true
 *   VUE_APP_UMAMI_SCRIPT_URL=https://<host-umami>/script.js
 *   VUE_APP_UMAMI_WEBSITE_ID=<uuid>
 *   VUE_APP_UMAMI_DEBUG=true    → log in console, utile senza account Umami
 */

/** Nomi degli eventi tracciati. Usare sempre queste costanti. */
export const EVENTS = {
  SESSION_STARTED: "demo_session_started",
  LOGIN_SUCCESS: "demo_login_success",
  LOGIN_FAILED: "demo_login_failed",
  DASHBOARD_VIEWED: "demo_dashboard_viewed",
  CUSTOMER_CREATED: "demo_customer_created",
  QUOTE_CREATED: "demo_quote_created",
  QUOTE_EXPORTED: "demo_quote_exported",
  ARTICLE_SEARCHED: "demo_article_searched",
  FEATURE_USED: "demo_feature_used",
};

const config = {
  enabled: process.env.VUE_APP_UMAMI_ENABLED === "true",
  scriptUrl: (process.env.VUE_APP_UMAMI_SCRIPT_URL || "").trim(),
  websiteId: (process.env.VUE_APP_UMAMI_WEBSITE_ID || "").trim(),
  debug: process.env.VUE_APP_UMAMI_DEBUG === "true",
};

/** Il tracking parte solo con tutti e tre i requisiti soddisfatti. */
export function isEnabled() {
  return Boolean(config.enabled && config.scriptUrl && config.websiteId);
}

// ---------------------------------------------------------------------------
// Sanificazione dei dati evento
// ---------------------------------------------------------------------------

/**
 * Chiavi ammesse negli eventi. Qualsiasi altra chiave viene scartata.
 * Sono tutte informazioni non sensibili: dove è avvenuta l'azione, quale
 * funzione è stata usata, com'è andata.
 */
const ALLOWED_KEYS = [
  "route", // pattern della rotta, es. /home/preventivi/modifica/:quoteId
  "feature", // nome tecnico della funzione, es. "export_pdf"
  "outcome", // "success" | "error" | "empty" | ...
  "action", // tipologia generica, es. "create" | "update" | "search"
  "kind", // sotto-tipo generico, es. "quote" | "order"
  "mode", // modalità, es. "demo"
  "reason", // causa categoriale di un errore, es. "invalid_credentials"
];

/**
 * Valori ammessi: stringhe corte "slug-like", booleani, interi piccoli.
 * Il formato esclude per costruzione email (@), spazi, testo libero, importi
 * con separatori decimali e stringhe lunghe.
 */
const SAFE_VALUE = /^[a-zA-Z0-9_\-:./]{1,48}$/;

/**
 * Filtra l'oggetto dati di un evento tenendo solo coppie chiave/valore sicure.
 * @param {Object} data
 * @returns {Object} oggetto ripulito (eventualmente vuoto)
 */
export function sanitizeEventData(data) {
  if (!data || typeof data !== "object") return {};

  const clean = {};
  for (const key of ALLOWED_KEYS) {
    if (!(key in data)) continue;
    const value = data[key];

    if (typeof value === "boolean") {
      clean[key] = value;
      continue;
    }
    if (typeof value === "number") {
      // Solo interi non negativi e piccoli: conteggi, mai importi.
      if (Number.isInteger(value) && value >= 0 && value <= 9999) {
        clean[key] = value;
      }
      continue;
    }
    if (typeof value === "string" && SAFE_VALUE.test(value)) {
      clean[key] = value;
    }
  }
  return clean;
}

// ---------------------------------------------------------------------------
// Caricamento script e coda eventi
// ---------------------------------------------------------------------------

let scriptRequested = false;

/**
 * Coda degli invii in attesa che lo script Umami sia pronto.
 *
 * Ogni elemento porta il proprio TIPO, perché eventi custom e pageview usano
 * API diverse e non sono intercambiabili:
 *
 *   { type: "event",    name: "demo_login_success", data: {...} }
 *   { type: "pageview", route: "/home/preventivi/modifica/:quoteId", title: "..." }
 *
 * Senza questa distinzione una pageview accodata verrebbe rispedita come
 * evento custom chiamato "pageview", falsando le statistiche.
 */
const queue = [];

/** Buffer ispezionabile da console: window.__demoAnalytics.log() */
const debugLog = [];

function log(...args) {
  if (config.debug) console.info("[analytics]", ...args);
}

/** Vero quando lo script Umami è caricato ed espone l'API di tracking. */
function umamiReady() {
  return Boolean(
    typeof window !== "undefined" &&
      window.umami &&
      typeof window.umami.track === "function"
  );
}

/** Svuota la coda rispettando il tipo di ogni elemento. */
function flushQueue() {
  // Si lavora su una copia: se un invio fallisce e riaccoda, non si cicla.
  const pending = queue.splice(0, queue.length);
  for (const entry of pending) {
    if (entry.type === "pageview") {
      sendPageView(entry.route, entry.title);
    } else {
      sendEvent(entry.name, entry.data);
    }
  }
}

/** Invia un evento custom, o lo accoda se Umami non è ancora pronto. */
function sendEvent(name, data) {
  if (!umamiReady()) {
    queue.push({ type: "event", name, data });
    return;
  }
  try {
    window.umami.track(name, data);
  } catch (err) {
    // Il tracking non deve mai interrompere l'applicazione.
    log("invio evento fallito:", err && err.message);
  }
}

/** Invia una pageview, o la accoda se Umami non è ancora pronto. */
function sendPageView(route, title) {
  if (!umamiReady()) {
    queue.push({ type: "pageview", route, title });
    return;
  }
  try {
    // API pageview di Umami: si passa una funzione che rimappa le proprietà
    // della pagina. `url` riceve il PATTERN della rotta, mai l'URL con gli ID.
    window.umami.track((props) => ({ ...props, url: route, title }));
  } catch (err) {
    log("invio pageview fallito:", err && err.message);
  }
}

/**
 * Inserisce nel documento lo script Umami, una sola volta.
 * L'auto-tracking è disattivato: le pagine viste sono inviate esplicitamente
 * da `trackPageView`, così viene registrato il pattern della rotta e non
 * l'URL con gli ID delle entità.
 */
function loadScript() {
  if (scriptRequested || !isEnabled()) return;
  scriptRequested = true;

  const el = document.createElement("script");
  el.async = true;
  el.defer = true;
  el.src = config.scriptUrl;
  el.setAttribute("data-website-id", config.websiteId);
  el.setAttribute("data-auto-track", "false");

  el.onload = () => {
    log("script Umami caricato");
    flushQueue();
  };
  el.onerror = () => {
    // Caso tipico in locale o con ad-blocker: si prosegue senza tracking.
    // La coda viene svuotata: senza Umami non c'è dove mandare gli eventi e
    // tenerli in memoria non servirebbe a nulla.
    log("script Umami non caricato: tracking disattivato per questa sessione");
    queue.length = 0;
  };

  document.head.appendChild(el);
}

// ---------------------------------------------------------------------------
// API pubblica
// ---------------------------------------------------------------------------

/**
 * Traccia un evento. Sicura da chiamare sempre: se il tracking è disattivato
 * o Umami non è disponibile, non fa nulla (a parte il log in debug).
 *
 * @param {string} name  uno dei valori di EVENTS
 * @param {Object} [data] metadati categoriali (vengono comunque sanificati)
 */
export function trackEvent(name, data = {}) {
  const payload = { mode: "demo", ...sanitizeEventData(data) };

  if (config.debug) {
    debugLog.push({
      type: "event",
      name,
      data: payload,
      at: new Date().toISOString(),
    });
    log("evento:", name, payload);
  }

  if (!isEnabled()) return;
  sendEvent(name, payload);
}

/**
 * Traccia una pagina vista usando il PATTERN della rotta (non l'URL reale),
 * così non finiscono negli analytics gli ID di clienti, preventivi o ordini.
 *
 * @param {import("vue-router").RouteLocationNormalized} route
 */
export function trackPageView(route) {
  const pattern = routePattern(route);
  // Il titolo è il nome della rotta: un'etichetta tecnica, mai un dato utente.
  const title = String((route && route.name) || pattern);

  if (config.debug) {
    debugLog.push({
      type: "pageview",
      route: pattern,
      title,
      at: new Date().toISOString(),
    });
    log("pageview:", pattern);
  }

  if (!isEnabled()) return;
  sendPageView(pattern, title);
}

/** Pattern della rotta corrente, es. "/home/preventivi/modifica/:quoteId". */
function routePattern(route) {
  const matched =
    route.matched && route.matched.length
      ? route.matched[route.matched.length - 1].path
      : route.path;
  return matched || "/";
}

/**
 * Inizializza il tracking: carica lo script (se configurato), registra la
 * navigazione SPA e invia l'evento di inizio sessione.
 *
 * @param {import("vue-router").Router} router
 */
export function initAnalytics(router) {
  // Metodo di debug sempre disponibile in console, anche senza Umami reale:
  //   window.__demoAnalytics.log()      → eventi raccolti
  //   window.__demoAnalytics.status()   → stato della configurazione
  window.__demoAnalytics = {
    log: () => debugLog.slice(),
    clear: () => {
      debugLog.length = 0;
    },
    // `status()` riporta solo stati booleani e conteggi: mai lo script URL,
    // mai il website ID, mai i valori di configurazione.
    status: () => ({
      enabled: isEnabled(),
      debug: config.debug,
      scriptConfigured: Boolean(config.scriptUrl),
      websiteIdConfigured: Boolean(config.websiteId),
      umamiLoaded: umamiReady(),
      queued: queue.length,
      queuedEvents: queue.filter((q) => q.type === "event").length,
      queuedPageViews: queue.filter((q) => q.type === "pageview").length,
    }),
    events: EVENTS,
  };

  if (!isEnabled()) {
    log(
      "tracking disattivato (configurazione incompleta o VUE_APP_UMAMI_ENABLED != true)"
    );
  }

  loadScript();

  if (router) {
    router.afterEach((to) => trackPageView(to));
  }

  trackEvent(EVENTS.SESSION_STARTED);
}

/**
 * Superficie riservata ai test unitari.
 *
 * Espone lo stato interno della coda per poter verificare che eventi custom e
 * pageview restino distinti e vengano inoltrati con l'API giusta. Non fa parte
 * dell'API pubblica: non usarla nei componenti.
 */
export const __testing = {
  /** Copia della coda in attesa. */
  getQueue: () => queue.map((e) => ({ ...e })),
  /** Svuota la coda inoltrando ogni elemento secondo il suo tipo. */
  flushQueue,
  /** Simula il fallimento del caricamento dello script. */
  dropQueue: () => {
    queue.length = 0;
  },
  /** Riporta il modulo allo stato iniziale fra un test e l'altro. */
  reset: () => {
    queue.length = 0;
    debugLog.length = 0;
    scriptRequested = false;
  },
  /** Inserisce lo script Umami (usata per verificarne gli attributi). */
  loadScript,
  config,
};

export default { initAnalytics, trackEvent, trackPageView, isEnabled, EVENTS };
